/**
 * GraphQL Client Setup
 *
 * Lightweight fetch-based GraphQL client with WebSocket subscriptions.
 * Uses graphql-ws for real-time updates.
 * Integrates with OIDC authentication via the auth module.
 *
 * Reference: MEMFAB-CTRL-SPEC Section 09 (Agent GraphQL API)
 */

import { createClient, Client as WSClient } from 'graphql-ws';
import { getAccessToken } from '../auth';
import { getGraphqlUrl, getSubscriptionUrl, getDevSessionKey } from '../config';
import { currentPreviewAs, PREVIEW_AS_HEADER } from '@/stores/personPreviewStore';

/**
 * Whether a GraphQL document is a mutation or a subscription: its first
 * definition, past comments and whitespace, starts with that keyword.
 */
export function isWriteDocument(doc: string): boolean {
  const stripped = doc.replace(/#[^\n]*/g, '').trimStart();
  return /^(mutation|subscription)\b/.test(stripped);
}

/** The refusal a person preview gives a write before it is sent (PP-4). */
export function previewReadOnlyError(): Error {
  return new Error('Read-only: previewing as another person; exit the preview to make changes');
}

export interface GraphQLResponse<T = unknown> {
  data?: T;
  errors?: Array<{
    message: string;
    path?: string[];
    extensions?: Record<string, unknown>;
  }>;
}

export interface GraphQLRequestOptions {
  variables?: Record<string, unknown>;
  headers?: Record<string, string>;
}

/** Default request timeout in milliseconds */
const REQUEST_TIMEOUT_MS = 15_000;

/** localStorage key for pending invite token */
const INVITE_TOKEN_KEY = 'ixos_invite_token';

/** localStorage key for invited principal's email (for mismatch error display) */
const INVITE_EMAIL_KEY = 'ixos_invite_email';

/**
 * Terminal authorization verdict codes returned by the backend (CDEV-840).
 * These come back as HTTP 403 — an authenticated identity that is not authorized.
 * They are NOT retryable: renewing the OIDC token cannot change the verdict, so the
 * app must surface a terminal screen, never a silent-renewal loop.
 */
/**
 * UNPROVEN_ADDRESS and BOUND_ELSEWHERE refuse an address invitation (CDEV-2455):
 * the sign-in has not proven the invited address, or already belongs to
 * someone else.
 */
export type AccessDeniedCode = 'WRONG_ACCOUNT' | 'INVITE_EXPIRED' | 'NOT_INVITED' | 'UNPROVEN_ADDRESS' | 'BOUND_ELSEWHERE';

export interface AccessDeniedDetail {
  code: AccessDeniedCode;
  message: string;
  /** Masked invited email (e.g. i***********7@gmail.com), or '' when none applies. */
  invitedEmail: string;
  authenticatedEmail: string;
}

/**
 * sessionStorage key: survives the OIDC callback page reload so the terminal screen persists.
 * Any authorized response clears a NOT_INVITED verdict (see graphqlRequest, CDEV-2522).
 */
export const ACCESS_DENIED_KEY = 'ixos_access_denied';

/**
 * sessionStorage key counting consecutive 401s across redirects. The auth layer caps
 * retries on this so a persistently-rejected session can't bounce the user forever; it
 * is cleared here the moment any request is authorized (see graphqlRequest).
 */
export const AUTH_401_COUNT_KEY = 'ixos_auth401_count';

/**
 * Parse a 403 response body into an AccessDeniedDetail, or null if it isn't a
 * recognizable authorization verdict. Handles the structured JSON body
 * ({error:"access_denied", code, message, invitedEmail, authenticatedEmail}) and,
 * for resilience during rollout, the legacy plain-text mismatch message.
 */
function parseAccessDenied(raw: string): AccessDeniedDetail | null {
  const invitedFallback = localStorage.getItem(INVITE_EMAIL_KEY) || '';
  try {
    const parsed = JSON.parse(raw);
    if (parsed && parsed.error === 'access_denied' && parsed.code) {
      return {
        code: parsed.code as AccessDeniedCode,
        message: typeof parsed.message === 'string' ? parsed.message : '',
        invitedEmail: parsed.invitedEmail || invitedFallback,
        authenticatedEmail: parsed.authenticatedEmail || '',
      };
    }
  } catch {
    // Not JSON — fall through to the legacy plain-text form.
  }
  // Legacy backend (pre-CDEV-840): "invite was sent to <x> but you authenticated as <y>".
  const match = raw.match(/invite was sent to (\S+) but you authenticated as (\S+)/);
  if (match) {
    return {
      code: 'WRONG_ACCOUNT',
      message: raw,
      invitedEmail: invitedFallback || match[1],
      authenticatedEmail: match[2],
    };
  }
  return null;
}

/**
 * Whether a stored access-denied value is settled by the server admitting the
 * session (CDEV-2522): a NOT_INVITED verdict, or a value that does not parse.
 * No stored value is nothing to clear.
 */
function isStaleDenial(stored: string | null): boolean {
  if (stored === null) return false;
  try {
    return (JSON.parse(stored) as Partial<AccessDeniedDetail> | null)?.code === 'NOT_INVITED';
  } catch {
    return true;
  }
}

/**
 * Execute a GraphQL query or mutation
 */
export async function graphqlRequest<T = unknown>(
  query: string,
  options: GraphQLRequestOptions = {}
): Promise<GraphQLResponse<T>> {
  const { variables, headers = {} } = options;

  // A person preview (CDEV-2234) is read-only: a write is refused here, before
  // it is sent, as the server would refuse it.
  const previewAs = currentPreviewAs();
  if (previewAs && isWriteDocument(query)) {
    throw previewReadOnlyError();
  }

  // Build headers with auth token if available
  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...headers,
  };

  // CDEV-404: dev-only session-key auth — send the bare keyname (no Bearer),
  // bypassing OIDC entirely. Hard-gated on import.meta.env.DEV inside
  // getDevSessionKey(), so this branch is dead code in a production build.
  const devSessionKey = getDevSessionKey();
  if (devSessionKey) {
    requestHeaders['Authorization'] = devSessionKey;
  } else {
    const token = getAccessToken();
    if (!token) {
      // No token available yet — the OIDC provider hasn't finished or
      // the user isn't authenticated.  Don't dispatch auth:unauthorized here;
      // that event is for server-side 401 rejections.  Firing it before auth
      // completes causes signinSilent() races that block the login redirect.
      throw new Error('Unauthorized — no valid token available');
    }
    requestHeaders['Authorization'] = `Bearer ${token}`;
  }

  // The previewee, while previewing (MEMFAB-PERSON-PREVIEW-ADDENDUM PP-1).
  if (previewAs) {
    requestHeaders[PREVIEW_AS_HEADER] = previewAs;
  }

  // Append invite token as query param if present (for first-login binding)
  let url = getGraphqlUrl();
  const inviteToken = localStorage.getItem(INVITE_TOKEN_KEY);
  if (inviteToken) {
    const separator = url.includes('?') ? '&' : '?';
    url += `${separator}invite=${encodeURIComponent(inviteToken)}`;
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: requestHeaders,
      body: JSON.stringify({
        query,
        variables,
      }),
      signal: controller.signal,
    });
  } catch (err: unknown) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new Error(`GraphQL request timed out after ${REQUEST_TIMEOUT_MS / 1000}s`);
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }

  if (response.status === 401) {
    // Token expired or missing — dispatch event so auth layer can trigger re-login.
    // This prevents the app from silently failing when silent renewal breaks.
    window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    throw new Error('Unauthorized — session may have expired');
  }

  if (response.status === 403) {
    // A terminal authorization verdict (CDEV-840): the JWT is valid but the identity
    // is not authorized. This is NOT retryable — renewing the token can never change
    // it — so we surface a terminal screen and MUST NOT dispatch auth:unauthorized
    // (which would trigger silent-renewal / redirect and loop forever).
    const raw = await response.text();
    const denied = parseAccessDenied(raw);
    if (denied) {
      // The invite token, if any, is now spent, wrong, or expired — clear it so a
      // subsequent clean login doesn't re-trigger the same verdict.
      localStorage.removeItem(INVITE_TOKEN_KEY);
      localStorage.removeItem(INVITE_EMAIL_KEY);
      // Persist across the OIDC callback reload (a full page load resets React state).
      sessionStorage.setItem(ACCESS_DENIED_KEY, JSON.stringify(denied));
      window.dispatchEvent(new CustomEvent('auth:access-denied', { detail: denied }));
      throw new Error(denied.message || 'Access denied');
    }
    // A 403 that isn't a recognized access-denied verdict — fall through to generic.
  }

  if (!response.ok) {
    throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
  }

  // The request was authorized (not 401/403) — the session works, so reset the
  // consecutive-401 counter the auth layer uses to cap its retry loop (CDEV-840).
  sessionStorage.removeItem(AUTH_401_COUNT_KEY);
  // The server admits this session, so a stored NOT_INVITED verdict is stale: an
  // operator approved the sign-in after the 403 was saved. Clear it and tell the
  // Shell, or the terminal screen outlives the approval (CDEV-2522). Only
  // NOT_INVITED: the invite verdicts (WRONG_ACCOUNT and the rest) are about an
  // invitation, and a success as some other existing principal does not settle them.
  if (isStaleDenial(sessionStorage.getItem(ACCESS_DENIED_KEY))) {
    sessionStorage.removeItem(ACCESS_DENIED_KEY);
    window.dispatchEvent(new CustomEvent('auth:access-granted'));
  }

  const result: GraphQLResponse<T> = await response.json();

  // Throw if GraphQL returned errors
  if (result.errors && result.errors.length > 0) {
    const errorMessages = result.errors.map(e => e.message).join(', ');
    throw new Error(`GraphQL errors: ${errorMessages}`);
  }

  // Invite token was accepted — clear it only on success so a backend
  // failure doesn't silently consume the token without creating the binding
  if (inviteToken) {
    localStorage.removeItem(INVITE_TOKEN_KEY);
    localStorage.removeItem(INVITE_EMAIL_KEY);
  }

  return result;
}

/**
 * Typed query helper
 */
export async function query<T = unknown>(
  queryDoc: string,
  variables?: Record<string, unknown>
): Promise<T> {
  const result = await graphqlRequest<T>(queryDoc, { variables });
  if (!result.data) {
    throw new Error('No data returned from query');
  }
  return result.data;
}

/**
 * Typed mutation helper
 */
export async function mutate<T = unknown>(
  mutationDoc: string,
  variables?: Record<string, unknown>
): Promise<T> {
  const result = await graphqlRequest<T>(mutationDoc, { variables });
  if (!result.data) {
    throw new Error('No data returned from mutation');
  }
  return result.data;
}

// WebSocket client singleton for subscriptions
let wsClient: WSClient | null = null;

/**
 * Get or create WebSocket client for subscriptions
 */
export function getWSClient(): WSClient {
  if (!wsClient) {
    wsClient = createClient({
      url: getSubscriptionUrl(),
      retryAttempts: 5,
      shouldRetry: () => true,
      // Pass auth token in connection params for subscriptions
      connectionParams: () => {
        const devSessionKey = getDevSessionKey();
        if (devSessionKey) return { authorization: devSessionKey };
        const token = getAccessToken();
        return token ? { authorization: `Bearer ${token}` } : {};
      },
      on: {
        connected: () => console.log('[GraphQL WS] Connected'),
        closed: () => console.log('[GraphQL WS] Closed'),
        error: (err) => console.error('[GraphQL WS] Error:', err),
      },
    });
  }
  return wsClient;
}

/**
 * Subscribe to a GraphQL subscription
 * Returns an unsubscribe function
 */
export function subscribe<T = unknown>(
  subscriptionDoc: string,
  variables: Record<string, unknown>,
  callbacks: {
    onData: (data: T) => void;
    onError?: (error: Error) => void;
    onComplete?: () => void;
  }
): () => void {
  // The subscription socket carries no person preview, so under one it would
  // stream the caller's own data as the previewed view: refused (PP-4).
  if (currentPreviewAs()) {
    callbacks.onError?.(previewReadOnlyError());
    return () => {};
  }
  const client = getWSClient();

  const unsubscribe = client.subscribe<T>(
    {
      query: subscriptionDoc,
      variables,
    },
    {
      next: (result) => {
        if (result.data) {
          callbacks.onData(result.data);
        }
        if (result.errors) {
          const errorMsg = result.errors.map(e => e.message).join(', ');
          callbacks.onError?.(new Error(errorMsg));
        }
      },
      error: (err) => {
        callbacks.onError?.(err instanceof Error ? err : new Error(String(err)));
      },
      complete: () => {
        callbacks.onComplete?.();
      },
    }
  );

  return unsubscribe;
}

/**
 * Cleanup WebSocket connection
 */
export function closeWSClient(): void {
  if (wsClient) {
    wsClient.dispose();
    wsClient = null;
  }
}

/**
 * Reset WebSocket client (e.g., on auth change)
 * Forces reconnection with new auth token
 */
export function resetWSClient(): void {
  closeWSClient();
  // Client will be recreated on next subscribe() call with new token
}

// Export typed client object for convenience
export const graphqlClient = {
  query,
  mutate,
  subscribe,
  close: closeWSClient,
  resetAuth: resetWSClient,
};

export default graphqlClient;
